// What broke in this browser, sent to the site (scenario O7,
// docs/design/PLAN-2026-10-09-error-reports.md).
//
// No server log sees a screen that threw on a phone. So the page tells the
// API (`POST /api/client-errors`), and the admin tool's Errors tab reads it:
// an exception nothing caught, a promise nothing waited on, a screen that
// crashed while drawing (`ScreenBoundary`), and an answer from the API in a
// shape this build does not read (`api.ts`).
//
// IT MUST NEVER BECOME THE ERROR. Everything is wrapped, the request is fire
// and forget and goes around `api.ts` — whose drift is one of the things
// reported, so a report through it could report itself — and two guards keep
// an error loop from becoming a flood: each failure once per page life, and
// ten in all. The endpoint caps a source on top of that.
//
// ON IN DEVELOPMENT TOO, which is the point: Ken's own dev browser reports to
// the dev database, and the admin tool's Dev switch reads it. Off under the
// test runner, where a thrown error is a test's business.

import { CLIENT_ERROR_LIMITS, type ClientErrorKind, type ClientErrorReport } from '@fsp/domain';
import { buildInfo } from '../buildInfo';
import { tierFor } from '../shell/useTier';

/** The most reports one page life sends. */
export const MOST_REPORTS = 10;

const seen = new Set<string>();
let sent = 0;

function enabled(): boolean {
  try {
    return import.meta.env.MODE !== 'test' && typeof window !== 'undefined';
  } catch {
    return false;
  }
}

function clip(text: string, most: number): string {
  return text.length > most ? `${text.slice(0, most - 1)}…` : text;
}

function setting(name: 'data-look' | 'data-detail'): string | null {
  const value = document.documentElement.getAttribute(name);
  return value === null || value === '' ? null : clip(value, CLIENT_ERROR_LIMITS.setting);
}

/** The errors a boundary reported as a crash, so the window's copy of one is not sent again. */
const claimed = new WeakSet<object>();

/**
 * How long the window's error event waits before reporting. React's
 * development build replays a render error it then hands to a boundary, so
 * the window hears a crash first; this is long enough for the boundary to
 * claim it, which keeps one crash one report.
 */
const CLAIM_MS = 250;

/** The report as sent: the stack cut further until the whole body fits the API's cap. */
function sized(report: ClientErrorReport): string {
  let body = JSON.stringify(report);
  let stack = report.stack ?? null;
  // Escaping and multi-byte characters make the body longer than its fields.
  while (new TextEncoder().encode(body).length > CLIENT_ERROR_LIMITS.body && stack !== null) {
    stack = stack.length > 200 ? `${stack.slice(0, Math.floor(stack.length / 2))}…` : null;
    body = JSON.stringify({ ...report, stack });
  }
  return body;
}

/**
 * Report one failure. Safe from anywhere, at any time, and never rejects:
 * answers whether the site took the report — false when it was a repeat, past
 * the page's ten, off, refused or unanswered. `error` is the thrown value,
 * for a boundary to claim it (`CLAIM_MS`).
 */
export function reportError(
  kind: ClientErrorKind,
  message: string,
  extra: { stack?: string | null; api?: string | null; error?: unknown } = {},
): Promise<boolean> {
  try {
    if (!enabled()) return Promise.resolve(false);
    if (kind === 'crash' && typeof extra.error === 'object' && extra.error !== null) {
      claimed.add(extra.error);
    }
    const text = clip(message.trim() || 'Unknown error', CLIENT_ERROR_LIMITS.message);
    const key = `${kind}\n${text}\n${window.location.pathname}`;
    if (seen.has(key) || sent >= MOST_REPORTS) return Promise.resolve(false);
    seen.add(key);
    sent += 1;
    const report: ClientErrorReport = {
      kind,
      message: text,
      stack: typeof extra.stack === 'string' ? clip(extra.stack, CLIENT_ERROR_LIMITS.stack) : null,
      // The path alone: a query string can carry an invite's code.
      path: clip(window.location.pathname || '/', CLIENT_ERROR_LIMITS.path),
      api: typeof extra.api === 'string' ? clip(extra.api, CLIENT_ERROR_LIMITS.api) : null,
      build: clip(buildInfo().sha || 'unknown', CLIENT_ERROR_LIMITS.build),
      tier: tierFor(window.innerWidth),
      installed: window.matchMedia?.('(display-mode: standalone)').matches === true,
      look: setting('data-look'),
      level: setting('data-detail'),
    };
    return fetch('/api/client-errors', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      // Outlives a page that is going away because of the very failure it reports.
      keepalive: true,
      body: sized(report),
    })
      .then((response) => response.status === 202)
      .catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
}

/** Listen for what nothing caught. Once, from `main.tsx`, before the first render. */
export function installErrorReporting(): void {
  try {
    if (!enabled()) return;
    window.addEventListener('error', (event) => {
      // A script or image that failed to load fires here too, with no
      // message and no error; that is the network's, not a bug's.
      if (!event.message && !(event.error instanceof Error)) return;
      // The error's own message where there is one: the event's is the
      // browser's sentence around it ("Uncaught Error: …" in Chrome, other
      // words elsewhere), which would split one failure by browser.
      const error: unknown = event.error;
      const message = error instanceof Error ? error.message : event.message;
      const stack = error instanceof Error ? error.stack : null;
      setTimeout(() => {
        // A crash a boundary caught is its report, not this one.
        if (typeof error === 'object' && error !== null && claimed.has(error)) return;
        void reportError('error', message, { stack });
      }, CLAIM_MS);
    });
    window.addEventListener('unhandledrejection', (event) => {
      const reason: unknown = event.reason;
      // An abort is somebody leaving a screen before its read came back.
      if (reason instanceof DOMException && reason.name === 'AbortError') return;
      if (reason instanceof Error)
        void reportError('rejection', reason.message, { stack: reason.stack });
      else void reportError('rejection', String(reason));
    });
  } catch {
    // A listener that cannot be added leaves the page as it was.
  }
}
